/**
 * viewportSync.js — Enterprise Viewport Synchronization Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 — ULTIMATE SYNC SUITE]:
 *  - enableMPRSync: динамические имена (итерация по всем ORTHOGRAPHIC)
 *  - enablePositionSync: ids не в замыкании, динамическое получение
 *  - jumpToWorldPosition: все ORTHOGRAPHIC + 3D
 *  - enableVOISync(viewportIds): синхронизация W/L между конкретными окнами
 *  - enableStackScrollSync: синхронизация скролла между стеками
 *  - syncAllViewports: включить все синхронизации разом
 *  - Cross-study registration (registerStudies)
 *  - Fusion sync (syncFusionWindowLevel)
 *  - Sync groups (createSyncGroup)
 *  - 3D camera sync (sync3DCameras)
 *
 * @module viewportSync
 */

import { getRenderingEngine, getViewportIds, cornerstone, ViewportType } from './viewportState.js';
import { planeNormal, projectOnNormal, analyzeDepths, nearestIndex } from './sliceDepth.js';  // [10.7.0] глубина среза — чистая арифметика, проверяется тестом
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { log } from '../utils/logger.js';

// Lazy imports
const _getViewportCore = () => import('./viewport.js');

/**
 * [12.0.57] ГДЕ СЛУШАТЬ СОБЫТИЯ ОКОН.
 *
 * Все синхронизации этого файла слушали cornerstone.eventTarget. А Cornerstone
 * 5.6 шлёт CAMERA_MODIFIED, VOI_MODIFIED и STACK_NEW_IMAGE на ЭЛЕМЕНТ окна
 * (triggerEvent(this.element, …), без всплытия) — до eventTarget они не
 * доходят никогда. На стенде «Спец. режимы»: «Связать окна» отвечала «Окна
 * связаны: 3 MPR», а окно W/L и листание не передавались ни в одно окно.
 * Тем же страдала и W/L-связка, которую раскладка MPR включает сама
 * (uiManager, [MPR-SYNC]): она не работала ни разу.
 *
 * Фаза погружения есть у любого события, всплывающего или нет: слушатель на
 * document с capture=true видит события всех окон, в том числе окон новой
 * раскладки, — подписываться на каждый элемент и переподписываться при смене
 * раскладки не нужно.
 */
const _слушать = (тип, h) => document.addEventListener(тип, h, true);
const _неСлушать = (тип, h) => document.removeEventListener(тип, h, true);

/** Том реформата (для «одна серия — одно окно W/L»). */
function _томОкна(vp) {
  try { return vp?.getVolumeId?.() || (vp?.getActors?.() || [])[0]?.referencedId || null; } catch (e) { return null; }
}

// ══════════════════════════════════════════════════════════════
//  VOI SYNC (W/L) — динамические имена
// ══════════════════════════════════════════════════════════════

let _voiSyncActive = false;
let _voiSyncHandler = null;
let _voiSyncViewportIds = null; // null = все ORTHOGRAPHIC

/**
 * Включить синхронизацию W/L между MPR-вьюпортами.
 * @param {string[]} [viewportIds] — конкретные ID (по умолчанию: все ORTHOGRAPHIC)
 */
export function enableMPRSync(viewportIds) {
  if (_voiSyncActive) {
    // Обновляем список если уже активно
    _voiSyncViewportIds = viewportIds || null;
    return;
  }
  _voiSyncActive = true;
  _voiSyncViewportIds = viewportIds || null;

  const re = getRenderingEngine();
  if (!re) return;

  let _syncing = false;

  _voiSyncHandler = (evt) => {
    if (_syncing) return;
    _syncing = true;
    try {
      const srcVpId = evt?.detail?.viewportId;
      const srcVp = re.getViewport(srcVpId);
      const voiRange = srcVp?.getProperties?.()?.voiRange;
      if (!voiRange) return;

      // Динамический список: если задан — используем, иначе все ORTHOGRAPHIC
      const targetIds = _voiSyncViewportIds || getViewportIds().filter(id => {
        const vp = re.getViewport(id);
        return vp?.type === ViewportType.ORTHOGRAPHIC;
      });

      // [12.0.57] Окно W/L — только окнам той же серии (того же тома): правило
      // 12.0.49 — КТ-окно не ложится на ПЭТ рядом. Прежде связка этого не
      // знала, но и не срабатывала ни разу (см. _слушать).
      if (srcVp?.type !== ViewportType.ORTHOGRAPHIC) return;
      const томИсточника = _томОкна(srcVp);
      for (const vpId of targetIds) {
        if (vpId === srcVpId) continue;
        const vp = re.getViewport(vpId);
        if (!vp?.setProperties || vp.type !== ViewportType.ORTHOGRAPHIC) continue;
        const том = _томОкна(vp);
        if (томИсточника && том && том !== томИсточника) continue;
        const было = vp.getProperties?.()?.voiRange;
        if (было && было.lower === voiRange.lower && было.upper === voiRange.upper) continue;
        vp.setProperties({ voiRange }); vp.render();
      }
    } finally { _syncing = false; }
  };

  try {
    const VOI_EVT = cornerstone.Enums?.Events?.VOI_MODIFIED || 'CORNERSTONE_VOI_MODIFIED';
    _слушать(VOI_EVT, _voiSyncHandler);   // [12.0.57] не eventTarget — см. _слушать
    log.debug(`[Sync] VOI sync enabled${_voiSyncViewportIds ? ` for [${_voiSyncViewportIds.join(',')}]` : ' (all ORTHOGRAPHIC)'}`);
  } catch (e) { _voiSyncActive = false; log.warn('[Sync] VOI error:', e.message); }
}

export function disableMPRSync() {
  if (!_voiSyncActive) return;
  _voiSyncActive = false;
  try {
    const VOI_EVT = cornerstone.Enums?.Events?.VOI_MODIFIED || 'CORNERSTONE_VOI_MODIFIED';
    _неСлушать(VOI_EVT, _voiSyncHandler);
  } catch (e) { log.debug("[viewportSync]", e.message); }
  _voiSyncHandler = null;
  _voiSyncViewportIds = null;
  log.debug('[Sync] VOI sync disabled');
}

/**
 * Синхронизация W/L между конкретными viewport'ами (не только MPR).
 * @param {string[]} viewportIds
 */
export function enableVOISync(viewportIds) {
  if (!viewportIds?.length) return;
  enableMPRSync(viewportIds);
}

// ══════════════════════════════════════════════════════════════
//  POSITION SYNC (динамические ids)
// ══════════════════════════════════════════════════════════════

let _posSynchronizer = null;
let _posSyncViewportIds = null;

export function enablePositionSync(viewportIds) {
  disablePositionSync();
  const re = getRenderingEngine();
  if (!re) return;

  _posSyncViewportIds = viewportIds || getViewportIds().filter(id => {
    const vp = re.getViewport(id);
    return vp?.type === ViewportType.ORTHOGRAPHIC;
  });

  if (_posSyncViewportIds.length < 2) return;

  const CAMERA_MODIFIED = cornerstone.Enums?.Events?.CAMERA_MODIFIED || 'CORNERSTONE_CAMERA_MODIFIED';
  let _syncing = false;

  const handler = (evt) => {
    if (_syncing) return;
    _syncing = true;
    try {
      const srcVpId = evt?.detail?.viewportId;
      const srcVp = re.getViewport(srcVpId);
      const srcCam = srcVp?.getCamera?.();
      if (!srcCam?.focalPoint) return;

      // Динамически получаем актуальные ID каждый раз
      const currentIds = _posSyncViewportIds || getViewportIds().filter(id => {
        const vp = re.getViewport(id);
        return vp?.type === ViewportType.ORTHOGRAPHIC;
      });

      // [12.0.57] Двигаются только окна С ТОЙ ЖЕ ОРИЕНТАЦИЕЙ (нормали
      // параллельны): «окна одного вида листаются вместе». Прежде (если бы
      // связка срабатывала — см. _слушать) каждое окно сдвигалось к точке
      // фокуса источника вдоль своей нормали: сдвиг аксиального окна мышью
      // перекидывал боковое и фронтальное на другие срезы, а листание бокового
      // уводило аксиальное на середину тома — фокус бокового окна стоит там.
      if (!currentIds.includes(srcVpId) || !srcCam.viewPlaneNormal) return;
      const n0 = srcCam.viewPlaneNormal;
      for (const vpId of currentIds) {
        if (vpId === srcVpId) continue;
        const vp = re.getViewport(vpId);
        if (!vp) continue;
        const cam = vp.getCamera?.();
        if (!cam?.viewPlaneNormal) continue;
        const vpn = cam.viewPlaneNormal;
        if (Math.abs(vpn[0] * n0[0] + vpn[1] * n0[1] + vpn[2] * n0[2]) < 0.99) continue;
        const fp = cam.focalPoint;
        const srcFp = srcCam.focalPoint;
        const dot = vpn[0] * (srcFp[0] - fp[0]) + vpn[1] * (srcFp[1] - fp[1]) + vpn[2] * (srcFp[2] - fp[2]);
        if (Math.abs(dot) < 1e-3) continue;
        vp.setCamera({
          focalPoint: [fp[0] + vpn[0] * dot, fp[1] + vpn[1] * dot, fp[2] + vpn[2] * dot],
          position: [(cam.position?.[0] || 0) + vpn[0] * dot, (cam.position?.[1] || 0) + vpn[1] * dot, (cam.position?.[2] || 0) + vpn[2] * dot],
        });
        vp.render();
      }
    } finally { _syncing = false; }
  };

  _слушать(CAMERA_MODIFIED, handler);   // [12.0.57] не eventTarget — см. _слушать
  _posSynchronizer = {
    handler,
    destroy() {
      _неСлушать(CAMERA_MODIFIED, handler);
    },
  };
  log.debug(`[Sync] Position sync enabled for ${_posSyncViewportIds.length} viewports`);
}

export function disablePositionSync() {
  if (_posSynchronizer) {
    try { _posSynchronizer.destroy(); } catch (e) { log.debug("[viewportSync]", e.message); }
    _posSynchronizer = null;
  }
  _posSyncViewportIds = null;
}

/** Jump all MPR + 3D viewports to world position */
export function jumpToWorldPosition(worldPos) {
  const re = getRenderingEngine();
  if (!re) return;

  // Все ORTHOGRAPHIC
  const ids = getViewportIds().filter(id => {
    const vp = re.getViewport(id);
    return vp?.type === ViewportType.ORTHOGRAPHIC;
  });

  for (const vpId of ids) {
    const vp = re.getViewport(vpId);
    if (!vp) continue;
    try {
      const cam = vp.getCamera();
      if (!cam?.viewPlaneNormal) continue;
      const vpn = cam.viewPlaneNormal;
      const fp = cam.focalPoint;
      const dist = vpn[0] * (worldPos[0] - fp[0]) + vpn[1] * (worldPos[1] - fp[1]) + vpn[2] * (worldPos[2] - fp[2]);
      vp.setCamera({
        focalPoint: [fp[0] + vpn[0] * dist, fp[1] + vpn[1] * dist, fp[2] + vpn[2] * dist],
        position: [(cam.position?.[0] || 0) + vpn[0] * dist, (cam.position?.[1] || 0) + vpn[1] * dist, (cam.position?.[2] || 0) + vpn[2] * dist],
      });
      vp.render();
    } catch (e) { log.debug("[viewportSync]", e.message); }
  }

  // 3D viewport
  const vp3dId = ids.find(id => re.getViewport(id)?.type === ViewportType.VOLUME_3D);
  if (vp3dId) {
    const vp3d = re.getViewport(vp3dId);
    if (vp3d) {
      try {
        const cam = vp3d.getCamera();
        if (cam?.focalPoint && cam?.position) {
          const dx = worldPos[0] - cam.focalPoint[0];
          const dy = worldPos[1] - cam.focalPoint[1];
          const dz = worldPos[2] - cam.focalPoint[2];
          vp3d.setCamera({
            focalPoint: [...worldPos],
            position: [cam.position[0] + dx, cam.position[1] + dy, cam.position[2] + dz],
          });
          vp3d.render();
        }
      } catch (e) { log.debug("[viewportSync]", e.message); }
    }
  }
}

// ══════════════════════════════════════════════════════════════
//  STACK SCROLL SYNC
// ══════════════════════════════════════════════════════════════

let _stackScrollSyncState = null;

/**
 * Синхронизация скролла между несколькими STACK-вьюпортами.
 * @param {string[]} viewportIds
 */
export function enableStackScrollSync(viewportIds) {
  disableStackScrollSync();
  const re = getRenderingEngine();
  if (!re) return;

  const ids = viewportIds || getViewportIds().filter(id => {
    const vp = re.getViewport(id);
    return vp?.type === ViewportType.STACK;
  });
  if (ids.length < 2) return;

  let _syncing = false;
  const STACK_EVT = cornerstone.Enums?.Events?.STACK_NEW_IMAGE || 'CORNERSTONE_STACK_NEW_IMAGE';

  const handler = (evt) => {
    if (_syncing) return;
    _syncing = true;
    try {
      const srcVpId = evt?.detail?.viewportId;
      if (!ids.includes(srcVpId)) return;
      const srcVp = re.getViewport(srcVpId);
      const srcIdx = srcVp?.getCurrentImageIdIndex?.() ?? 0;
      const srcTotal = srcVp?.getImageIds?.()?.length || 1;
      const pct = srcIdx / Math.max(srcTotal - 1, 1);
      const плоскостьИсточника = _плоскостьСнимка(srcVp?.getImageIds?.()?.[srcIdx]);

      for (const vpId of ids) {
        if (vpId === srcVpId) continue;
        const vp = re.getViewport(vpId);
        if (!vp || vp.type !== ViewportType.STACK) continue;
        const total = vp.getImageIds?.()?.length || 1;
        // [12.0.57] Та же система координат и параллельные срезы — к срезу
        // на той же анатомической глубине; иначе — по доле серии, как было.
        const поГлубине = _срезНаГлубине(vp, плоскостьИсточника);
        const targetIdx = поГлубине ?? Math.round(pct * (total - 1));
        if (targetIdx === (vp.getCurrentImageIdIndex?.() ?? -1)) continue;
        try {
          vp.setImageIdIndex(targetIdx);
          vp.render();
        } catch (e) { log.debug("[viewportSync stack]", e.message); }
      }
    } finally { _syncing = false; }
  };

  _слушать(STACK_EVT, handler);   // [12.0.57] не eventTarget — см. _слушать
  _stackScrollSyncState = {
    handler,
    viewportIds: ids,
    destroy() { _неСлушать(STACK_EVT, handler); },
  };
  log.debug(`[Sync] Stack scroll sync enabled for ${ids.length} viewports`);
}

/** [12.0.57] Плоскость снимка: нормаль, глубина, система координат. */
function _плоскостьСнимка(imageId) {
  if (!imageId) return null;
  try {
    const п = cornerstone.metaData.get('imagePlaneModule', imageId);
    const n = planeNormal(п?.imageOrientationPatient);
    const d = projectOnNormal(п?.imagePositionPatient, n);
    if (!n || d == null) return null;
    return { n, d, for: п.frameOfReferenceUID || null };
  } catch (e) { return null; }
}

/** [12.0.57] Номер среза стопки на той же глубине, что источник, — или null. */
function _срезНаГлубине(vp, источник) {
  if (!источник) return null;
  const ids = vp.getImageIds?.() || [];
  const первый = _плоскостьСнимка(ids[0]);
  if (!первый || !первый.for || первый.for !== источник.for) return null;
  if (Math.abs(первый.n[0] * источник.n[0] + первый.n[1] * источник.n[1] + первый.n[2] * источник.n[2]) < 0.99) return null;
  const глубины = ids.map((id) => _плоскостьСнимка(id)?.d);
  if (глубины.some((g) => g == null)) return null;
  const знак = Math.sign(первый.n[0] * источник.n[0] + первый.n[1] * источник.n[1] + первый.n[2] * источник.n[2]) || 1;
  return nearestIndex(глубины, источник.d * знак);
}

export function disableStackScrollSync() {
  if (_stackScrollSyncState) {
    try { _stackScrollSyncState.destroy(); } catch (e) { log.debug("[viewportSync]", e.message); }
    _stackScrollSyncState = null;
  }
}

// ══════════════════════════════════════════════════════════════
//  3D CAMERA SYNC
// ══════════════════════════════════════════════════════════════

let _3dCameraSyncState = null;

/**
 * Синхронизация камер между несколькими 3D-вьюпортами.
 * @param {string[]} viewportIds — ID 3D-вьюпортов
 */
export function sync3DCameras(viewportIds) {
  unsync3DCameras();
  if (!viewportIds?.length || viewportIds.length < 2) return;

  const re = getRenderingEngine();
  if (!re) return;

  const CAMERA_MODIFIED = cornerstone.Enums?.Events?.CAMERA_MODIFIED || 'CORNERSTONE_CAMERA_MODIFIED';
  let _syncing = false;

  const handler = (evt) => {
    if (_syncing) return;
    _syncing = true;
    try {
      const srcVpId = evt?.detail?.viewportId;
      if (!viewportIds.includes(srcVpId)) return;
      const srcVp = re.getViewport(srcVpId);
      const srcCam = srcVp?.getCamera?.();
      if (!srcCam) return;

      for (const vpId of viewportIds) {
        if (vpId === srcVpId) continue;
        const vp = re.getViewport(vpId);
        if (!vp) continue;
        try {
          vp.setCamera(srcCam);
          vp.render();
        } catch (e) { log.debug("[viewportSync 3d]", e.message); }
      }
    } finally { _syncing = false; }
  };

  _слушать(CAMERA_MODIFIED, handler);   // [12.0.57] не eventTarget — см. _слушать
  _3dCameraSyncState = {
    handler,
    viewportIds,
    destroy() { _неСлушать(CAMERA_MODIFIED, handler); },
  };
  log.debug(`[Sync] 3D camera sync enabled for [${viewportIds.join(', ')}]`);
}

export function unsync3DCameras() {
  if (_3dCameraSyncState) {
    try { _3dCameraSyncState.destroy(); } catch (e) { log.debug("[viewportSync]", e.message); }
    _3dCameraSyncState = null;
  }
}

// ══════════════════════════════════════════════════════════════
//  FUSION SYNC (W/L для overlay)
// ══════════════════════════════════════════════════════════════

/**
 * Синхронизировать W/L между основным томом и overlay (PET/CT).
 * @param {string} viewportId
 */
export function syncFusionWindowLevel(viewportId) {
  const re = getRenderingEngine();
  const vp = re?.getViewport(viewportId);
  if (!vp) return;

  const VOI_EVT = cornerstone.Enums?.Events?.VOI_MODIFIED || 'CORNERSTONE_VOI_MODIFIED';
  let _syncing = false;

  const handler = (evt) => {
    if (_syncing || evt?.detail?.viewportId !== viewportId) return;
    _syncing = true;
    try {
      const voiRange = vp.getProperties?.()?.voiRange;
      if (!voiRange) return;
      const actors = vp.getActors?.();
      if (actors?.length > 1) {
        const prop = actors[1].actor?.getProperty?.();
        if (prop) {
          const cfun = prop.getRGBTransferFunction(0);
          cfun.setMappingRange(voiRange.lower, voiRange.upper);
          vp.render();
        }
      }
    } finally { _syncing = false; }
  };

  _слушать(VOI_EVT, handler);   // [12.0.57] не eventTarget — см. _слушать (вызывающих сейчас нет)
  return {
    destroy() { _неСлушать(VOI_EVT, handler); },
  };
}

// ══════════════════════════════════════════════════════════════
//  SYNC GROUPS
// ══════════════════════════════════════════════════════════════

const _syncGroups = new Map(); // groupName → { voi, position, scroll, camera3d }

/**
 * Создать именованную группу синхронизации.
 * @param {string} groupName
 * @param {Object} opts — { voi?: string[], position?: string[], scroll?: string[], camera3d?: string[] }
 */
export function createSyncGroup(groupName, opts = {}) {
  // Очищаем старую группу
  destroySyncGroup(groupName);

  const group = {};

  if (opts.voi?.length >= 2) {
    enableVOISync(opts.voi);
    group.voi = opts.voi;
  }
  if (opts.position?.length >= 2) {
    enablePositionSync(opts.position);
    group.position = opts.position;
  }
  if (opts.scroll?.length >= 2) {
    enableStackScrollSync(opts.scroll);
    group.scroll = opts.scroll;
  }
  if (opts.camera3d?.length >= 2) {
    sync3DCameras(opts.camera3d);
    group.camera3d = opts.camera3d;
  }

  _syncGroups.set(groupName, group);
  log.debug(`[Sync] Group "${groupName}" created`);
  return group;
}

export function destroySyncGroup(groupName) {
  const group = _syncGroups.get(groupName);
  if (!group) return;
  if (group.voi) disableMPRSync();
  if (group.position) disablePositionSync();
  if (group.scroll) disableStackScrollSync();
  if (group.camera3d) unsync3DCameras();
  _syncGroups.delete(groupName);
  log.debug(`[Sync] Group "${groupName}" destroyed`);
}

// ══════════════════════════════════════════════════════════════
//  CROSS-STUDY REGISTRATION
// ══════════════════════════════════════════════════════════════

/**
 * Автоматическая регистрация двух исследований по DICOM-тегам.
 * Находит соответствие срезов по ImagePositionPatient.
 *
 * @param {string[]} imageIds1 — ID изображений первого исследования
 * @param {string[]} imageIds2 — ID изображений второго исследования
 * @returns {{ mapping: Map<number, number>, offsetMm: number }}
 */
export function registerStudies(imageIds1, imageIds2) {
  if (!imageIds1?.length || !imageIds2?.length) return null;

  // Извлекаем позиции срезов
  const getPosition = (imageId) => {
    try {
      const pl = cornerstone.metaData.get('imagePlaneModule', imageId);
      if (!pl?.imagePositionPatient) return null;
      const ipp = pl.imagePositionPatient;
      const rc = pl.rowCosines, cc = pl.columnCosines;
      if (!rc || !cc) return ipp[2]; // fallback: Z-координата
      // Проекция на нормаль
      const normal = [
        rc[1] * cc[2] - rc[2] * cc[1],
        rc[2] * cc[0] - rc[0] * cc[2],
        rc[0] * cc[1] - rc[1] * cc[0],
      ];
      return ipp[0] * normal[0] + ipp[1] * normal[1] + ipp[2] * normal[2];
    } catch { return null; }
  };

  const positions1 = imageIds1.map(id => getPosition(id));
  const positions2 = imageIds2.map(id => getPosition(id));

  // Находим смещение между сериями
  const valid1 = positions1.filter(p => p != null);
  const valid2 = positions2.filter(p => p != null);
  if (!valid1.length || !valid2.length) return null;

  const offsetMm = valid2[0] - valid1[0];

  // Строим маппинг
  const mapping = new Map();
  for (let i = 0; i < positions1.length; i++) {
    if (positions1[i] == null) continue;
    let bestJ = -1, bestDist = Infinity;
    for (let j = 0; j < positions2.length; j++) {
      if (positions2[j] == null) continue;
      const dist = Math.abs(positions1[i] - positions2[j]);
      if (dist < bestDist && dist < 1.0) { // порог 1мм
        bestDist = dist;
        bestJ = j;
      }
    }
    if (bestJ >= 0) mapping.set(i, bestJ);
  }

  return { mapping, offsetMm: +offsetMm.toFixed(2), count1: imageIds1.length, count2: imageIds2.length };
}

// ══════════════════════════════════════════════════════════════
//  SYNC ALL
// ══════════════════════════════════════════════════════════════

/**
 * Включить все синхронизации разом для текущей раскладки.
 */
export function syncAllViewports() {
  // [11.1.1] Возвращаем СВОДКУ, а не ничего. Прежде функция молча выходила при
  // отсутствии движка и молча заканчивала при успехе — вызывающей стороне было
  // нечего сказать человеку, и кнопка выглядела мёртвой.
  const re = getRenderingEngine();
  if (!re) return { mpr: 0, stack: 0, vol3d: 0, причина: 'движок отрисовки не поднят' };

  const orthoIds = getViewportIds().filter(id => re.getViewport(id)?.type === ViewportType.ORTHOGRAPHIC);
  const stackIds = getViewportIds().filter(id => re.getViewport(id)?.type === ViewportType.STACK);
  const vol3dIds = getViewportIds().filter(id => re.getViewport(id)?.type === ViewportType.VOLUME_3D);

  if (orthoIds.length >= 2) {
    enableMPRSync(orthoIds);
    enablePositionSync(orthoIds);
  }
  if (stackIds.length >= 2) {
    enableStackScrollSync(stackIds);
  }
  if (vol3dIds.length >= 2) {
    sync3DCameras(vol3dIds);
  }

  log.debug(`[Sync] All sync enabled: ${orthoIds.length} MPR, ${stackIds.length} stack, ${vol3dIds.length} 3D`);
  // [12.0.57] Сколько окон реформата листаются вместе: только окна с одной
  // ориентацией (см. enablePositionSync). В MPR три плоскости разные — у них
  // общее только окно W/L, и кнопка должна сказать это, а не «связаны».
  const нормали = orthoIds.map((id) => { try { return re.getViewport(id).getCamera().viewPlaneNormal; } catch (e) { return null; } });
  let листаютсяВместе = 0;
  нормали.forEach((n, i) => {
    if (!n) return;
    if (нормали.some((m, j) => j !== i && m && Math.abs(n[0] * m[0] + n[1] * m[1] + n[2] * m[2]) >= 0.99)) листаютсяВместе++;
  });
  // Считаем только то, что ДЕЙСТВИТЕЛЬНО связано: одно окно связывать не с чем.
  return {
    mpr: orthoIds.length >= 2 ? orthoIds.length : 0,
    mprЛистание: orthoIds.length >= 2 ? листаютсяВместе : 0,
    stack: stackIds.length >= 2 ? stackIds.length : 0,
    vol3d: vol3dIds.length >= 2 ? vol3dIds.length : 0,
  };
}

// ══════════════════════════════════════════════════════════════
//  SMART COMPARE
// ══════════════════════════════════════════════════════════════

let _compareState = null;

export async function startSmartCompare(containerId, study1, study2, opts = {}) {
  try {
    stopSmartCompare();
    const { setupViewports, loadStack } = await _getViewportCore();
    const viewportIds = await setupViewports(containerId, '1x2');
    if (viewportIds.length < 2) throw new Error('Need ≥2 viewports');
    const [vpId1, vpId2] = viewportIds;
    const re = getRenderingEngine();

    if (study1.imageIds?.length) await loadStack(vpId1, study1.imageIds);
    if (study2.imageIds?.length) await loadStack(vpId2, study2.imageIds);

    const handlers = [];

    // Scroll sync через StackScrollSync (вместо wheel)
    if (opts.syncScroll !== false) {
      enableStackScrollSync([vpId1, vpId2]);
      handlers.push(() => disableStackScrollSync());
    }

    // VOI sync через конкретные ID
    if (opts.syncWL !== false) {
      enableVOISync([vpId1, vpId2]);
      handlers.push(() => disableMPRSync());
    }

    // Регистрация исследований
    let registration = null;
    if (study1.imageIds?.length && study2.imageIds?.length) {
      registration = registerStudies(study1.imageIds, study2.imageIds);
      if (registration) {
        log.debug(`[Compare] Registered: ${registration.mapping.size} slices mapped`);
      }
    }

    try {
      const l1 = document.querySelector(`#viewport-${vpId1} .vp-orientation-label`);
      const l2 = document.querySelector(`#viewport-${vpId2} .vp-orientation-label`);
      if (l1) l1.textContent = study1.label || 'Study 1';
      if (l2) l2.textContent = study2.label || 'Study 2';
    } catch (e) { log.debug("[viewportSync]", e.message); }

    _compareState = {
      viewportIds: [vpId1, vpId2],
      handlers,
      syncWL: opts.syncWL !== false,
      registration,
    };

    events.emit(EVENTS.SMART_COMPARE_SYNC, { action: 'start', viewportIds: [vpId1, vpId2], registration });
    return _compareState;
  } catch (e) { log.warn(`[viewportSync] startSmartCompare failed:`, e.message); throw e; }
}

export function stopSmartCompare() {
  if (!_compareState) return;
  _compareState.handlers.forEach(fn => { try { fn(); } catch (e) { log.debug("[viewportSync]", e.message); } });
  _compareState = null;
  events.emit(EVENTS.SMART_COMPARE_SYNC, { action: 'stop' });
}

// ══════════════════════════════════════════════════════════════
//  [10.7.0] СВЯЗАННАЯ ПРОКРУТКА ДВУХ ИССЛЕДОВАНИЙ
// ══════════════════════════════════════════════════════════════
//
// Почему нельзя взять готовый enableStackScrollSync. Он сопоставляет окна по
// ПРОЦЕНТУ глубины: тридцатый срез из ста — это 30%, значит в соседнем окне
// тоже 30%. Для двух серий одного исследования это верно. Для двух РАЗНЫХ
// исследований — нет, и ошибка тем грубее, чем полезнее сравнение.
//
// Пример из жизни. КТ снята от ключиц до диафрагмы, МРТ — только сердце.
// Середина КТ — это середина грудной клетки, середина МРТ — середина сердца.
// Проценты совпали, анатомия разошлась на десять сантиметров, и врач
// сравнивает разные органы, не понимая почему.
//
// Правильная опора одна — координаты пациента. У каждого среза в DICOM есть
// положение в пространстве (ImagePositionPatient) и ориентация плоскости
// (ImageOrientationPatient). Берём точку текущего среза одного окна и ищем в
// другом тот срез, чья плоскость к этой точке ближе всего. Тогда совпадает
// анатомический уровень, а не доля прокрутки, — и разное число срезов, разная
// толщина и разный охват перестают мешать.
//
// Если геометрии нет (а она есть не у всего — например, у рентгеновского
// снимка), честно откатываемся на проценты: приблизительно лучше, чем никак.

let _compareSyncState = null;

// ── [10.11.0] Привязка двух исследований друг к другу ────────────────
//
// Я связал окна «по координате пациента» и назвал это анатомическим уровнем.
// Внутри одного исследования это верно. Между двумя РАЗНЫМИ сессиями — нет.
//
// ImagePositionPatient отсчитывается от стола томографа, а не от анатомии. В
// июле человек лежал на столе в одном положении, в августе в другом, и у
// одного и того же среза координата разная — часто на сантиметры. Врач это и
// увидел: окна разъехались.
//
// Отсюда две поправки, и они дополняют друг друга.
//
// Первая — ручная: врач прокручивает оба окна на один ориентир и говорит
// «связать здесь». Мы запоминаем разницу глубин и дальше её учитываем. Так
// делают руками в любой станции, и это работает всегда, даже когда совмещение
// не удалось.
//
// Вторая — через совмещение: оно вычисляет, как повернулась и сместилась
// голова, и тогда точку из одного исследования можно перевести в другое
// целиком, а не поправкой вдоль одной оси.
// Счётчик срабатываний. Нужен не для красоты: «связь настроена» и «связь
// сработала» — разные вещи, и отличить их иначе нечем. На этом я уже
// обжёгся: проверка двигала срез вызовом из кода, событие не поднималось,
// связь молчала — а проверка проходила.
let _срабатываний = 0;
let _событий = 0;
let _сдвиг = {};            // "vpA→vpB" → поправка в мм вдоль нормали цели
let _переносТочки = null;   // (точка, откуда, куда) → точка, от совмещения

/**
 * Ручная привязка: запомнить, что текущие срезы обоих окон — один уровень.
 * @param {string[]} viewportIds
 * @returns {{ok: boolean, мм?: number, why?: string}}
 */
export function alignCompareHere(viewportIds) {
  const re = getRenderingEngine();
  const ids = viewportIds || _compareSyncState?.viewportIds || [];
  if (!re || ids.length < 2) return { ok: false, why: 'нужны два окна' };

  const точка = (vpId) => {
    const vp = re.getViewport(vpId);
    const список = vp?.getImageIds?.() || [];
    const i = vp?.getCurrentImageIdIndex?.() ?? 0;
    return список[i] ? _геометрияСреза(список[i]) : null;
  };
  const нормаль = (vpId) => {
    const vp = re.getViewport(vpId);
    for (const id of (vp?.getImageIds?.() || [])) {
      const г = _геометрияСреза(id);
      const n = г?.iop ? planeNormal(г.iop) : null;
      if (n) return n;
    }
    return null;
  };

  const [a, b] = ids;
  const гa = точка(a), гb = точка(b);
  const na = нормаль(a), nb = нормаль(b);
  if (!гa || !гb || !na || !nb) return { ok: false, why: 'у срезов нет геометрии' };

  // Обе поправки считаем сразу, а не выводим одну из другой: нормали двух
  // съёмок совпадают лишь приблизительно, и «минус то же самое» было бы
  // молчаливым допущением.
  const пр = (ipp, n) => ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2];
  _сдвиг[`${a}→${b}`] = пр(гb.ipp, nb) - пр(гa.ipp, nb);
  _сдвиг[`${b}→${a}`] = пр(гa.ipp, na) - пр(гb.ipp, na);

  const мм = _сдвиг[`${a}→${b}`];
  log.info(`[Sync] привязка вручную: смещение ${мм.toFixed(1)} мм`);
  return { ok: true, мм };
}

/** Снять ручную привязку — вернуться к сырым координатам. */
export function clearCompareAlignment() {
  _сдвиг = {};
  log.info('[Sync] ручная привязка снята');
}

/**
 * [12.0.11] Привязка от НАЙДЕННОЙ пары срезов — для «Связать по уровню».
 *
 * Та же арифметика, что в alignCompareHere, с одним отличием: уровни не
 * берутся с текущих позиций окон, а даны снаружи (парой imageId срезов,
 * которые подбор по профилям счёл одним анатомическим уровнем). Обе поправки
 * считаются из настоящей геометрии обеих съёмок — по той же причине, что и
 * там: нормали двух съёмок совпадают лишь приблизительно.
 */
export function setCompareAlignmentFromPair(vpA, vpB, imageIdA, imageIdB) {
  const re = getRenderingEngine();
  if (!re) return { ok: false, why: 'нет движка отрисовки' };
  // Нормаль окна — как внутри alignCompareHere: первый срез стопки с годной
  // геометрией.
  const нормаль = (vpId) => {
    const vp = re.getViewport(vpId);
    for (const id of (vp?.getImageIds?.() || [])) {
      const г = _геометрияСреза(id);
      const n = г?.iop ? planeNormal(г.iop) : null;
      if (n) return n;
    }
    return null;
  };
  const гa = _геометрияСреза(imageIdA), гb = _геометрияСреза(imageIdB);
  const na = нормаль(vpA), nb = нормаль(vpB);
  if (!гa || !гb || !na || !nb) return { ok: false, why: 'у срезов нет геометрии' };

  const пр = (ipp, n) => ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2];
  _сдвиг[`${vpA}→${vpB}`] = пр(гb.ipp, nb) - пр(гa.ipp, nb);
  _сдвиг[`${vpB}→${vpA}`] = пр(гa.ipp, na) - пр(гb.ipp, na);

  const мм = _сдвиг[`${vpA}→${vpB}`];
  log.info(`[Sync] привязка по найденному уровню: смещение ${мм.toFixed(1)} мм`);
  return { ok: true, мм };
}

/**
 * Задать перенос точки между исследованиями (от совмещения).
 * @param {null | ((точка: number[], откуда: string, куда: string) => number[])} fn
 */
export function setCompareTransform(fn) {
  _переносТочки = (typeof fn === 'function') ? fn : null;
  log.info(`[Sync] перенос по совмещению ${_переносТочки ? 'включён' : 'выключен'}`);
}

/**
 * Перенести точку из одного окна сравнения в другое — тем же правилом, каким
 * связана прокрутка.
 *
 * [10.12.0] Нужно указателю: врач ведёт мышью в одном окне, а во втором должна
 * появиться отметка в том же месте тела. Правило пересчёта обязано быть ОДНО с
 * прокруткой, иначе отметка покажет одно, а листание уведёт в другое — и
 * доверять нельзя ни тому, ни другому.
 *
 * @param {number[]} точка — мировые координаты (мм)
 * @param {string} откуда @param {string} куда
 * @returns {number[]|null}
 */
export function mapComparePoint(точка, откуда, куда) {
  return mapComparePointInfo(точка, откуда, куда).мир;
}

/**
 * [10.19.1] То же, но с ОТВЕТОМ, каким правилом точка перенесена:
 * 'совмещение' | 'привязка' | 'тождество'. Указателю всё равно, а измерению
 * объёма — нет: молчаливое тождество между разными исследованиями означает
 * промах на укладку, и вызывающий обязан иметь возможность отказаться.
 */
export function mapComparePointInfo(точка, откуда, куда) {
  if (!Array.isArray(точка) || точка.length < 3) return { мир: null, способ: 'нет' };
  const окна = _compareSyncState?.viewportIds || [];

  if (_переносТочки) {
    try {
      const п = _переносТочки(точка, откуда, куда, окна);
      if (п) return { мир: п, способ: 'совмещение' };
    } catch (e) { log.debug('[Sync] перенос точки:', e.message); }
    return { мир: точка, способ: 'тождество' };
  }

  // Ручная привязка — поправка вдоль нормали целевого окна. В плоскости среза
  // она ничего не меняет, и это верно: врач сводил окна по уровню, а не
  // двигал изображение вбок.
  const мм = _сдвиг[`${откуда}→${куда}`] || 0;
  if (!мм) return { мир: точка, способ: 'тождество' };
  const re = getRenderingEngine();
  const vp = re?.getViewport(куда);
  for (const id of (vp?.getImageIds?.() || [])) {
    const г = _геометрияСреза(id);
    const n = г?.iop ? planeNormal(г.iop) : null;
    if (n) return { мир: [точка[0] + n[0] * мм, точка[1] + n[1] * мм, точка[2] + n[2] * мм], способ: 'привязка' };
  }
  return { мир: точка, способ: 'тождество' };
}

/** Чем сейчас связаны окна — для отчёта врачу и для проверок. */
export function getCompareAlignment() {
  return {
    сдвиг: { ..._сдвиг }, поСовмещению: !!_переносТочки,
    срабатываний: _срабатываний, событий: _событий,
    связьЖива: !!_compareSyncState, окна: _compareSyncState?.viewportIds || [],
  };
}

function _геометрияСреза(imageId) {
  try {
    const pm = cornerstone.metaData.get('imagePlaneModule', imageId);
    const ipp = pm?.imagePositionPatient;
    const iop = pm?.imageOrientationPatient;
    if (!Array.isArray(ipp) || ipp.length < 3) return null;
    return { ipp: ipp.map(Number), iop: Array.isArray(iop) ? iop.map(Number) : null };
  } catch (e) { return null; }
}

/**
 * Разложить стопку окна по глубине: для каждого среза — его положение вдоль
 * собственной нормали. Считается один раз при включении, а не на каждом
 * повороте колеса: на пяти тысячах срезов это разница между плавной
 * прокруткой и рывками.
 */
function _разложитьПоГлубине(vp) {
  const ids = vp?.getImageIds?.() || [];
  if (!ids.length) return null;

  let нормаль = null;
  for (const id of ids) {
    const г = _геометрияСреза(id);
    if (г?.iop) { нормаль = planeNormal(г.iop); if (нормаль) break; }
  }
  if (!нормаль) return null;

  const глубины = new Array(ids.length);
  for (let i = 0; i < ids.length; i++) {
    const г = _геометрияСреза(ids[i]);
    const d = г ? projectOnNormal(г.ipp, нормаль) : null;
    if (d === null) return null;             // дыра в геометрии — уходим на проценты
    глубины[i] = d;
  }

  return { ids, нормаль, глубины, порядок: analyzeDepths(глубины) };
}

/**
 * Связать прокрутку двух окон по анатомическому уровню.
 * @param {string[]} viewportIds — ровно два окна со стопками срезов
 * @returns {{mode: 'position'|'percent'|'none'}} чем связали — это врач видит на экране
 */
export function enableCompareSync(viewportIds) {
  disableCompareSync();
  const re = getRenderingEngine();
  if (!re || !Array.isArray(viewportIds) || viewportIds.length < 2) return { mode: 'none' };

  const разложения = new Map();
  let поПоложению = true;
  for (const id of viewportIds) {
    const vp = re.getViewport(id);
    const р = vp ? _разложитьПоГлубине(vp) : null;
    if (!р) { поПоложению = false; break; }
    разложения.set(id, р);
  }

  let _идёт = false;
  // [10.11.0] Что мы сами только что поставили — чтобы не принять за движение
  // врача. Флага «идёт обработка» не хватило: Cornerstone поднимает событие
  // после загрузки кадра, то есть уже после выхода из обработчика, и окна
  // начинали гонять друг друга по кругу. Помним поставленное явно и гасим
  // ровно своё эхо, а не всё подряд по таймеру.
  const _поставлено = new Map();   // vpId → индекс, который поставили мы
  const STACK_EVT = cornerstone.Enums?.Events?.STACK_NEW_IMAGE || 'CORNERSTONE_STACK_NEW_IMAGE';

  const handler = (evt) => {
    _событий++;                                   // событий вообще, до отбора
    if (_идёт) return;
    const srcVpId = evt?.detail?.viewportId;
    if (!viewportIds.includes(srcVpId)) return;   // чужое окно — не наше дело

    // Наше собственное эхо: это движение мы и сделали секунду назад.
    const ожид = _поставлено.get(srcVpId);
    if (ожид !== undefined) {
      const тек = re.getViewport(srcVpId)?.getCurrentImageIdIndex?.();
      _поставлено.delete(srcVpId);
      if (тек === ожид) return;
    }

    _срабатываний++;
    _идёт = true;
    try {
      const srcVp = re.getViewport(srcVpId);
      const srcIdx = srcVp?.getCurrentImageIdIndex?.() ?? 0;

      for (const vpId of viewportIds) {
        if (vpId === srcVpId) continue;
        const vp = re.getViewport(vpId);
        if (!vp?.setImageIdIndex) continue;
        const всего = vp.getImageIds?.()?.length || 1;
        let цель;

        if (поПоложению) {
          // Точку текущего среза проецируем на нормаль ЦЕЛЕВОГО окна: так
          // сравнение работает и когда исследования сняты под разным углом.
          const срез = _геометрияСреза(srcVp.getImageIds()[srcIdx]);
          const целевое = разложения.get(vpId);
          let точка = срез?.ipp || null;
          // [10.11.0] Если совмещение посчитано — переносим точку целиком.
          // Если нет, но врач привязал вручную — учитываем его поправку.
          if (точка && _переносТочки) {
            // Порядок окон передаём явно: перенос должен знать, какое из них
            // опорное, иначе обратное направление пойдёт прямым.
            try { точка = _переносТочки(точка, srcVpId, vpId, viewportIds) || точка; }
            catch (e) { log.debug('[Sync] перенос:', e.message); }
          }
          let глубина = точка ? projectOnNormal(точка, целевое?.нормаль) : null;
          if (глубина !== null && !_переносТочки) {
            глубина += (_сдвиг[`${srcVpId}→${vpId}`] || 0);
          }
          if (глубина !== null) цель = nearestIndex(целевое.глубины, глубина, целевое.порядок);
        }
        if (цель === undefined) {
          const srcВсего = srcVp?.getImageIds?.()?.length || 1;
          цель = Math.round((srcIdx / Math.max(srcВсего - 1, 1)) * (всего - 1));
        }

        цель = Math.max(0, Math.min(всего - 1, цель));
        if (цель === vp.getCurrentImageIdIndex?.()) continue;   // уже там — не дёргаем
        _поставлено.set(vpId, цель);
        try { vp.setImageIdIndex(цель); vp.render(); }
        catch (e) { log.debug('[Sync] сравнение:', e.message); }
      }
    } finally { _идёт = false; }
  };

  // [10.11.0] СЛУШАЕМ ЭЛЕМЕНТЫ ОКОН, а не общую шину.
  //
  // Здесь стояло `cornerstone.eventTarget.addEventListener(...)` — и связь не
  // срабатывала НИ РАЗУ. Cornerstone шлёт STACK_NEW_IMAGE на элемент окна, а
  // не на общую шину, поэтому обработчик просто никогда не звали. Ошибки при
  // этом нет: подписка оформлена, объект живёт, в журнале «прокрутка связана».
  //
  // Почему это не поймалось раньше — отдельный урок. Проверка крутила колесо в
  // левом окне и ждала, что правое встанет на срез 0. Оба окна и так стояли на
  // нуле, так что «не двинулось» и «встало правильно» давали один и тот же
  // ответ. Проверка, которая не умеет провалиться, ничего не проверяет.
  // Поймано счётчиком событий: ноль срабатываний при четырнадцати поворотах
  // колеса.
  //
  // Общую шину оставляем запасным путём: в других сборках Cornerstone часть
  // событий уходит и туда, и лишняя подписка ничего не стоит — повторный вызов
  // отсекается флагом _идёт.
  const подписки = [];
  for (const vpId of viewportIds) {
    const el = re.getViewport(vpId)?.element;
    if (!el) continue;
    el.addEventListener(STACK_EVT, handler);
    подписки.push(() => el.removeEventListener(STACK_EVT, handler));
  }
  cornerstone.eventTarget.addEventListener(STACK_EVT, handler);
  подписки.push(() => cornerstone.eventTarget.removeEventListener(STACK_EVT, handler));
  log.debug(`[Sync] сравнение: подписок на события среза — ${подписки.length}`);

  _compareSyncState = {
    viewportIds: [...viewportIds],
    mode: поПоложению ? 'position' : 'percent',
    destroy() { подписки.forEach((снять) => { try { снять(); } catch (e) { /* */ } }); },
  };
  log.info(`[Sync] сравнение: прокрутка связана ${поПоложению ? 'по координате пациента' : 'по доле глубины (геометрии нет)'}`);
  return { mode: _compareSyncState.mode };
}

export function disableCompareSync() {
  _сдвиг = {};
  _переносТочки = null;
  _срабатываний = 0;
  _событий = 0;
  if (!_compareSyncState) return;
  try { _compareSyncState.destroy(); } catch (e) { log.debug('[viewportSync]', e.message); }
  _compareSyncState = null;
}

export const isCompareSyncActive = () => !!_compareSyncState;

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyAllSync() {
  disableCompareSync();
  disableMPRSync();
  disablePositionSync();
  disableStackScrollSync();
  unsync3DCameras();
  stopSmartCompare();
  for (const name of _syncGroups.keys()) destroySyncGroup(name);
}